import React, { useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import { FaPrint, FaArrowLeft, FaDownload } from "react-icons/fa";
import axiosInstance from "../config/axios";
import { buildCsv, downloadCsvFile } from "../utils/csvDownload";
import logo from "../Assets/logo3.jpg";

const COMPANY = "ABC Realty Inc., Brokerage";

const GL_ACCOUNT_TITLES = {
  "40100": "Total Commission Income",
  "51100": "Commission to Other Brokers",
  "50100": "Agent's Commission",
  "44100": "Brokerage Commission",
};

function getLedgerRowDate(entry) {
  if (entry.chequeDate) return new Date(entry.chequeDate);
  if (entry.date) {
    if (typeof entry.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(entry.date)) {
      const [y, m, d] = entry.date.split("-");
      return new Date(Number(y), Number(m) - 1, Number(d));
    }
    return new Date(entry.date);
  }
  if (entry.createdAt) return new Date(entry.createdAt);
  return null;
}

function formatLedgerRowDate(entry) {
  const d = getLedgerRowDate(entry);
  if (!d || Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-CA", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

/** Income-statement API flips the sign on 51100 for P&L presentation; G/L detail shows the natural balance. */
function display51100Amount(apiValue) {
  return Math.abs(Number(apiValue) || 0);
}

/** Pull line/subtotal values from income-statement API response (same ledger logic as Finance Statements). */
function extractActivityRows(statement) {
  const items = (statement || []).filter(Boolean);
  const line = (acct) =>
    items.find((i) => i.type === "line" && i.acct === acct)?.value ?? 0;
  const gross = items.find(
    (i) =>
      i.type === "subtotal" &&
      String(i.label || "").toUpperCase() === "GROSS COMMISSION EARNED",
  )?.value ?? 0;
  return {
    gl40100: line("40100"),
    gl51100: line("51100"),
    grossCommissionEarned: gross,
    gl50100: line("50100"),
    gl44100: line("44100"),
  };
}

const FinancialReportsActivityDetails = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isFinancePage = pathname === "/finance-activity-details";
  const printRef = useRef();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showReport, setShowReport] = useState(false);
  const [rows, setRows] = useState(null);
  const [ledgerModal, setLedgerModal] = useState({
    open: false,
    accountNumber: null,
    loading: false,
    entries: [],
    error: "",
  });

  const formatCurrency = (n) =>
    new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 2,
    }).format(Number(n) || 0);

  const formatDebitCredit = (n) => {
    const v = Number(n) || 0;
    if (v === 0) return "—";
    return formatCurrency(v);
  };

  const openLedgerModal = useCallback(
    async (accountNumber) => {
      if (!fromDate || !toDate) return;
      setLedgerModal({
        open: true,
        accountNumber,
        loading: true,
        entries: [],
        error: "",
      });
      try {
        const res = await axiosInstance.get("/ledger", {
          params: { from: fromDate, to: toDate },
        });
        const fromApi = (res.data || []).filter(
          (e) => String(e.accountNumber) === String(accountNumber),
        );

        const rawLocal = JSON.parse(
          localStorage.getItem("trialBalanceTransactions") || "[]",
        );
        const startDate = new Date(fromDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(toDate);
        endDate.setHours(23, 59, 59, 999);

        const fromLocal = rawLocal.filter((entry) => {
          const d = entry.date
            ? new Date(entry.date)
            : new Date(entry.createdAt || 0);
          return (
            d >= startDate &&
            d <= endDate &&
            String(entry.accountNumber) === String(accountNumber)
          );
        });

        const combined = [...fromApi, ...fromLocal];
        combined.sort((a, b) => {
          const ta = getLedgerRowDate(a)?.getTime() ?? 0;
          const tb = getLedgerRowDate(b)?.getTime() ?? 0;
          return tb - ta;
        });

        setLedgerModal({
          open: true,
          accountNumber,
          loading: false,
          entries: combined,
          error: "",
        });
      } catch (e) {
        console.error(e);
        setLedgerModal({
          open: true,
          accountNumber,
          loading: false,
          entries: [],
          error: "Could not load ledger transactions.",
        });
      }
    },
    [fromDate, toDate],
  );

  const closeLedgerModal = useCallback(() => {
    setLedgerModal({
      open: false,
      accountNumber: null,
      loading: false,
      entries: [],
      error: "",
    });
  }, []);

  const handleGenerate = async () => {
    if (!fromDate || !toDate) {
      alert("Please select both From and To dates.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const localStorageTransactions = JSON.parse(
        localStorage.getItem("trialBalanceTransactions") || "[]",
      );
      const startDate = new Date(fromDate);
      startDate.setUTCHours(0, 0, 0, 0);
      const endDate = new Date(toDate);
      endDate.setUTCHours(23, 59, 59, 999);

      const filteredLocal = localStorageTransactions.filter((entry) => {
        const entryDate = entry.date
          ? new Date(entry.date)
          : new Date(entry.createdAt || 0);
        return entryDate >= startDate && entryDate <= endDate;
      });

      const response = await axiosInstance.post(
        "/transactions/income-statement",
        { localStorageTransactions: filteredLocal },
        { params: { from: fromDate, to: toDate } },
      );

      const data = extractActivityRows(response.data?.statement);
      setRows(data);
      setShowReport(true);
    } catch (e) {
      console.error(e);
      setError("Could not load activity details. Please try again.");
      setRows(null);
      setShowReport(false);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const handleDownloadCsv = () => {
    if (!rows) return;
    const period = `${fromDate}_to_${toDate}`;
    const metaCsv = buildCsv(
      ["Report", "Value"],
      [
        ["Activity Details Report", COMPANY],
        ["Period", `${fromDate} to ${toDate}`],
      ],
    );
    const dataCsv = buildCsv(
      ["Description", "Amount"],
      [
        [
          "G/L 40100 Total Commission Income",
          (Number(rows.gl40100) || 0).toFixed(2),
        ],
        [
          "G/L 51100 Commission to Other Brokers (deduction)",
          display51100Amount(rows.gl51100).toFixed(2),
        ],
        [
          "Gross Commission Earned",
          (Number(rows.grossCommissionEarned) || 0).toFixed(2),
        ],
        [
          "G/L 50100 Agent's Commission",
          (Number(rows.gl50100) || 0).toFixed(2),
        ],
        [
          "G/L 44100 Brokerage Commission",
          (Number(rows.gl44100) || 0).toFixed(2),
        ],
      ],
    );
    const csv = `${metaCsv}\r\n\r\n${dataCsv}`;
    downloadCsvFile(`activity-details-report_${period}.csv`, csv);
  };

  const displayRows = rows
    ? [
        {
          key: "40100",
          glAccount: "40100",
          labelRest: " Total Commission Income",
          value: rows.gl40100,
          variant: "line",
        },
        {
          key: "51100",
          glAccount: "51100",
          labelRest: " Commission to Other Brokers (−)",
          value: display51100Amount(rows.gl51100),
          variant: "line",
        },
        {
          key: "gross",
          label: "Gross Commission Earned",
          value: rows.grossCommissionEarned,
          variant: "subtotal",
        },
        {
          key: "50100",
          glAccount: "50100",
          labelRest: " Agent's Commission",
          value: rows.gl50100,
          variant: "line",
        },
        {
          key: "44100",
          glAccount: "44100",
          labelRest: " Brokerage Commission",
          value: rows.gl44100,
          variant: "line",
        },
      ]
    : [];

  const mainInner = (
    <>
      <div className="max-w-3xl mx-auto">
          <div className="mb-6 no-print">
            {!isFinancePage && (
              <button
                type="button"
                onClick={() => navigate("/financial-reports")}
                className="flex items-center text-blue-900 hover:text-blue-700 mb-4"
              >
                <FaArrowLeft className="mr-2" />
                Back to Financial Reports
              </button>
            )}
            <h1 className="text-2xl font-bold text-gray-900">
              Activity Details Report
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Commission activity by general ledger account for the selected
              period (same basis as the income statement). After generating,
              click a <span className="font-medium">G/L account number</span> in
              the table to open ledger detail for that period.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 no-print mb-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Period</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From date
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To date
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleGenerate}
                disabled={loading || !fromDate || !toDate}
                className="bg-blue-900 text-white px-6 py-2 rounded-md hover:bg-blue-800 disabled:opacity-50"
              >
                {loading ? "Generating…" : "Generate report"}
              </button>
            </div>
          </div>

          {error && (
            <div className="no-print bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {showReport && rows && (
            <div
              ref={printRef}
              className="print-area bg-white rounded-lg shadow-md p-8 border border-gray-100"
            >
              <div className="flex justify-between items-start mb-6 no-print">
                <div />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handlePrint}
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
                  >
                    <FaPrint />
                    Print
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadCsv}
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                  >
                    <FaDownload />
                    CSV
                  </button>
                </div>
              </div>

              <div className="text-center mb-8">
                <img
                  src={logo}
                  alt=""
                  className="mx-auto max-h-16 mb-3 print:block"
                />
                <div className="text-lg font-semibold text-gray-900">
                  {COMPANY}
                </div>
                <div className="text-xl font-bold text-gray-900 mt-2">
                  Activity Details Report
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Period: {fromDate} to {toDate}
                </div>
              </div>

              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-800">
                    <th className="text-left py-2 pr-4 font-semibold text-gray-900">
                      Description
                    </th>
                    <th className="text-right py-2 pl-4 font-semibold text-gray-900 w-40">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((r) => (
                    <tr
                      key={r.key}
                      className={
                        r.variant === "subtotal"
                          ? "border-t-2 border-b-2 border-gray-800 bg-gray-50"
                          : "border-b border-gray-200"
                      }
                    >
                      <td
                        className={`py-3 pr-4 text-gray-900 ${
                          r.variant === "subtotal" ? "font-semibold" : ""
                        }`}
                      >
                        {r.glAccount ? (
                          <>
                            <button
                              type="button"
                              onClick={() => openLedgerModal(r.glAccount)}
                              className="text-blue-700 hover:text-blue-900 hover:underline font-medium print:text-gray-900 print:no-underline"
                            >
                              G/L {r.glAccount}
                            </button>
                            <span>{r.labelRest}</span>
                          </>
                        ) : (
                          r.label
                        )}
                      </td>
                      <td
                        className={`py-3 pl-4 text-right tabular-nums ${
                          r.variant === "subtotal" ? "font-semibold" : ""
                        }`}
                      >
                        {formatCurrency(r.value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="mt-8 border-t border-gray-300 pt-4 text-xs text-gray-500 print:block">
                Amounts follow the general ledger for the period (including
                entries from trial balance local storage, if any).
              </div>
            </div>
          )}
      </div>
    </>
  );

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <style>
        {`
          @media print {
            .no-print { display: none !important; }
            .print-area { box-shadow: none !important; }
          }
        `}
      </style>
      <div className="no-print">
        <Navbar />
      </div>
      {isFinancePage ? (
        <div className="flex flex-1">
          <div className="flex-1 p-6">{mainInner}</div>
        </div>
      ) : (
        <div className="flex-1 p-6">{mainInner}</div>
      )}

      {ledgerModal.open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 no-print"
          role="dialog"
          aria-modal="true"
          aria-labelledby="activity-ledger-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeLedgerModal();
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-start gap-4 px-5 py-4 border-b border-gray-200 shrink-0">
              <div>
                <h2
                  id="activity-ledger-modal-title"
                  className="text-lg font-semibold text-gray-900"
                >
                  G/L {ledgerModal.accountNumber}
                  {GL_ACCOUNT_TITLES[ledgerModal.accountNumber]
                    ? ` — ${GL_ACCOUNT_TITLES[ledgerModal.accountNumber]}`
                    : ""}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Period: {fromDate} to {toDate}
                </p>
              </div>
              <button
                type="button"
                onClick={closeLedgerModal}
                className="text-gray-500 hover:text-gray-800 text-2xl leading-none px-2"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="overflow-auto flex-1 p-4 min-h-[120px]">
              {ledgerModal.loading ? (
                <p className="text-gray-600 text-center py-8">Loading…</p>
              ) : ledgerModal.error ? (
                <p className="text-red-700 text-center py-4">
                  {ledgerModal.error}
                </p>
              ) : ledgerModal.entries.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No transactions for this account in the selected period.
                </p>
              ) : (
                <table className="min-w-full text-sm border border-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3 border-b font-semibold text-gray-700">
                        Date
                      </th>
                      <th className="text-left py-2 px-3 border-b font-semibold text-gray-700">
                        Description
                      </th>
                      <th className="text-left py-2 px-3 border-b font-semibold text-gray-700">
                        Reference
                      </th>
                      <th className="text-right py-2 px-3 border-b font-semibold text-gray-700">
                        Debit
                      </th>
                      <th className="text-right py-2 px-3 border-b font-semibold text-gray-700">
                        Credit
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerModal.entries.map((row, i) => (
                      <tr
                        key={
                          row._id
                            ? String(row._id)
                            : `row-${i}-${row.description?.slice(0, 20)}`
                        }
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="py-2 px-3 whitespace-nowrap text-gray-800">
                          {formatLedgerRowDate(row)}
                        </td>
                        <td className="py-2 px-3 text-gray-800 max-w-md">
                          {row.description || "—"}
                        </td>
                        <td className="py-2 px-3 whitespace-nowrap text-gray-700">
                          {row.reference || row.eftNumber || row.apNumber || "—"}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums text-gray-900">
                          {formatDebitCredit(row.debit)}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums text-gray-900">
                          {formatDebitCredit(row.credit)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialReportsActivityDetails;
